import React, { useState, useEffect } from 'react';
import './DocumentsList.css';
import { ReactComponent as DocumentIcon } from '../../assets/icons/document.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/export.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye.svg';
import Button from '../Button/Button';
import { formatDate } from '../../utils/entityUtils';

interface DocumentType {
  id: number;
  type: string;
  commentaire?: string;
  updated_at: string;
  key?: string; // Clé UUID pour les documents entreprise et accord-cadre
  category?: 'entreprise' | 'accord_cadre' | 'commun'; // Catégorie du document
  filename?: string;
  binaire?: string; // Contenu base64 pour les contrats AC
}

interface DocumentsListProps {
  documents: DocumentType[];
  variant?: 'fullWidth' | 'inline';
  lineheight?: number;
  showIcon?: boolean; // Afficher l'icône du document (default: true)
  size?: 'default' | 'smaller'; // Taille des lignes (default: 'default')
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  variant = 'inline',
  lineheight,
  showIcon = true,
  size = 'default'
}) => {
  // Afficher uniquement les documents passés en props (pas de fusion avec documents communs)
  const [allDocs, setAllDocs] = useState<DocumentType[]>([]);
  const [previewDoc, setPreviewDoc] = useState<DocumentType | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [loadingDocKey, setLoadingDocKey] = useState<string | null>(null);

  // Fonction utilitaire pour générer une clé unique pour un document
  const getDocumentUniqueKey = (doc: DocumentType): string => {
    return doc.key || `${doc.category || 'unknown'}-${doc.id}`;
  };

  // 👇 Fonction pour formater le type
  const formatDocumentType = (type: string): string => {
    if (!type) return '';

    // Si le type contient "CCTP" au début (ou tout seul), on remplace les '-' par des espaces
    if (type.toUpperCase().startsWith('CCTP')) {
      return type.replace(/-/g, ' ').toUpperCase();
    }

    // Sinon on laisse le reste tel quel, juste en majuscules
    return type.toUpperCase();
  };

  // 👇 Utiliser directement les documents passés en props (plus de fusion avec contexte)
  useEffect(() => {
    // Dédupliquer par clé unique
    const seenKeys = new Set<string>();
    const deduplicated: DocumentType[] = [];

    for (const doc of documents) {
      const uniqueKey = getDocumentUniqueKey(doc);
      if (!seenKeys.has(uniqueKey)) {
        seenKeys.add(uniqueKey);
        deduplicated.push(doc);
      }
    }

    setAllDocs(deduplicated);
  }, [documents]);

  useEffect(() => {
    if (previewBlobUrl) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [previewBlobUrl]);

  const handleDownload = async (doc: DocumentType) => {
    const docKey = getDocumentUniqueKey(doc);
    setLoadingDocKey(docKey);
    try {
      // Si le document a un binaire (base64), télécharger directement
      if (doc.binaire) {
        const byteCharacters = atob(doc.binaire);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = doc.filename || `${formatDocumentType(doc.type)}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
        setLoadingDocKey(null);
        return;
      }

      // Sinon, déterminer l'URL en fonction du type de document
      let url: string;

      if (doc.type.startsWith('Contrat')) {
        // Ancien comportement pour les contrats
        url = `${process.env.REACT_APP_API_URL}/contrats/${doc.id}/pdf`;
      } else if (doc.type.toLowerCase().startsWith('cctp')) {
        // Documents communs (CCTP)
        url = `${process.env.REACT_APP_API_URL}/documents-communs/${doc.id}/download`;
      } else if (doc.key) {
        // Documents entreprise ou accord-cadre avec clé UUID
        url = `${process.env.REACT_APP_API_URL}/documents/key/${doc.key}/download`;
      } else {
        // Fallback pour les anciens documents sans clé
        url = `${process.env.REACT_APP_API_URL}/documents/${doc.id}/download`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      if (!res.ok) throw new Error('Erreur téléchargement');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      // Utiliser le filename si disponible, sinon le type formaté
      link.download = doc.filename || `${formatDocumentType(doc.type)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDocKey(null);
    }
  };

  const handlePreview = async (doc: DocumentType) => {
    const docKey = getDocumentUniqueKey(doc);
    setLoadingDocKey(docKey);
    try {
      // Si le document a un binaire (base64), prévisualiser directement
      if (doc.binaire) {
        const byteCharacters = atob(doc.binaire);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        setPreviewBlobUrl(blobUrl);
        setPreviewDoc(doc);
        setLoadingDocKey(null);
        return;
      }

      // Sinon, déterminer l'URL en fonction du type de document
      let url: string;

      if (doc.type.startsWith('Contrat')) {
        // Ancien comportement pour les contrats
        url = `${process.env.REACT_APP_API_URL}/contrats/${doc.id}/pdf`;
      } else if (doc.type.toLowerCase().startsWith('cctp')) {
        // Documents communs (CCTP)
        url = `${process.env.REACT_APP_API_URL}/documents-communs/${doc.id}/download`;
      } else if (doc.key) {
        // Documents entreprise ou accord-cadre avec clé UUID
        url = `${process.env.REACT_APP_API_URL}/documents/key/${doc.key}/download`;
      } else {
        // Fallback pour les anciens documents sans clé
        url = `${process.env.REACT_APP_API_URL}/documents/${doc.id}/download`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      if (!res.ok) throw new Error('Erreur prévisualisation');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(blobUrl);
      setPreviewDoc(doc);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDocKey(null);
    }
  };

  const closeModal = () => {
    setPreviewBlobUrl(null);
    setPreviewDoc(null);
  };

  return (
    <div className="documents-list">
      <div className={`documents-grid documents-grid-${variant} ${size === 'smaller' ? 'documents-grid-smaller' : ''}`}>
        {allDocs.map((doc) => {
          const docKey = getDocumentUniqueKey(doc);
          const isLoading = loadingDocKey === docKey;
          return (
            <div key={docKey} className={`document-card document-card-${variant} ${size === 'smaller' ? 'document-card-smaller' : ''}`} onClick={() => handlePreview(doc)}>
              {/* En mode smaller + loading, afficher uniquement le spinner */}
              {size === 'smaller' && isLoading ? (
                <div className="document-loading-centered">
                  <div className="spinner spinner-smaller"></div>
                </div>
              ) : (
                <>
                  <div className="document-left">
                    {showIcon && <DocumentIcon className={`document-icon ${size === 'smaller' ? 'document-icon-smaller' : ''}`} />}
                    <div className="document-info">
                      <span className={`document-type ${size === 'smaller' ? 'document-type-smaller' : ''}`}>{formatDocumentType(doc.type)}</span>
                      <span className={`document-date ${size === 'smaller' ? 'document-date-smaller' : ''}`}>
                        Dernière mise à jour le {formatDate(new Date(doc.updated_at))}
                      </span>
                    </div>
                  </div>
                  {/* En mode default, le loader s'affiche à côté */}
                  {isLoading && size !== 'smaller' && (
                    <div className="document-loading">
                      <div className="spinner"></div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {previewBlobUrl && previewDoc && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content-document" onClick={(e) => e.stopPropagation()}>
            <h3>{formatDocumentType(previewDoc.type)}</h3>
            <iframe
              src={previewBlobUrl}
              title={previewDoc.type}
              width="100%"
              height="500px"
            />
            <div className="modal-actions">
              <Button
                onClick={() => handleDownload(previewDoc)}
                icon={<DownloadIcon className="dl-button-icon-white" />}
                value="Télécharger"
                size="small"
              />
              <Button
                onClick={closeModal}
                value="Fermer"
                size="small"
                variant="secondary"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsList;